'use client';

import { Tv, Film, Clock, TrendingUp, Star, CheckCircle2, PlayCircle, Calendar } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { WatchStats, formatWatchTime } from '@/lib/profileUtils';

interface StatsGridProps {
  stats: WatchStats;
  className?: string;
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subtext?: string;
  color: string;
}

function StatCard({ icon, label, value, subtext, color }: StatCardProps) {
  return (
    <Card className="bg-gray-900/50 border-gray-800 p-6 hover:bg-gray-900/70 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
              {icon}
            </div>
            <p className="text-sm font-medium text-gray-400">{label}</p>
          </div>
          <div className="space-y-1">
            <p className="text-3xl font-bold text-white">{value}</p>
            {subtext && (
              <p className="text-xs text-gray-500">{subtext}</p>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

export function StatsGrid({ stats, className = '' }: StatsGridProps) {
  return (
    <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 ${className}`}>
      <StatCard
        icon={<Tv className="h-5 w-5 text-red-400" />}
        label="Total in Watchlist"
        value={stats.totalItems}
        subtext={`${stats.totalTVShows} shows, ${stats.totalMovies} movies`}
        color="bg-red-600/10"
      />

      <StatCard
        icon={<PlayCircle className="h-5 w-5 text-green-400" />}
        label="Currently Watching"
        value={stats.activeShows}
        subtext={stats.activeShows === 1 ? 'active show' : 'active shows'}
        color="bg-green-600/10"
      />

      <StatCard
        icon={<CheckCircle2 className="h-5 w-5 text-blue-400" />}
        label="Completed Shows"
        value={stats.completedShows}
        subtext={`${stats.totalSeasonsWatched} seasons watched`}
        color="bg-blue-600/10"
      />

      <StatCard
        icon={<Film className="h-5 w-5 text-purple-400" />}
        label="Movies Watched"
        value={stats.watchedMovies}
        subtext={`${stats.unwatchedMovies} unwatched`}
        color="bg-purple-600/10"
      />

      <StatCard
        icon={<Clock className="h-5 w-5 text-yellow-400" />}
        label="Time Watched"
        value={formatWatchTime(stats.estimatedHoursWatched)}
        subtext={`~${stats.totalEpisodesWatched} episodes, ${stats.watchedMovies} movies`}
        color="bg-yellow-600/10"
      />

      <StatCard
        icon={<Star className="h-5 w-5 text-orange-400" />}
        label="Average Rating"
        value={stats.averageRating > 0 ? stats.averageRating.toFixed(1) : '—'}
        subtext={stats.ratedItems > 0 ? `${stats.ratedItems} rated` : 'No ratings yet'}
        color="bg-orange-600/10"
      />

      <StatCard
        icon={<TrendingUp className="h-5 w-5 text-indigo-400" />}
        label="Total Seasons"
        value={stats.totalSeasonsWatched}
        subtext="across all shows"
        color="bg-indigo-600/10"
      />

      <StatCard
        icon={<Calendar className="h-5 w-5 text-pink-400" />}
        label="Collection"
        value={`${stats.totalTVShows + stats.totalMovies}`}
        subtext="total titles"
        color="bg-pink-600/10"
      />
    </div>
  );
}
